// useCreatorDataFromDB.ts
//
// Fetches live creator data from operational Supabase tables and maps it into
// the CreatorData shape used by the template sections.
//
// Follows HTML-to-Template Protocol v2 (Phase 2 dynamic mapping).
//
// Data sources:
//   user                      → bio (name, avatar, tagline, social links)
//   user_links                → links[]  (only rows of the top-index
//                                          `links-section` stayuptodate component)
//   product_bank              → products[]
//   stayuptodate_components   → carousels[] (template_components via getTemplateComponents)
//                               + linkInBioStack[] (live link-in-bio via getStayUpToDateComponents)
//                               + campaign detection (for Newsletter auto-hide per §3.3)
//   About "More from"         → template_components content tabs only (see aboutMoreFromSection.ts)
//   blog_posts                → journal[] (§3.1.2 — schema logged on first fetch)
//   content_instagram         → carousel item pool (reused for content carousels)
//
// HasData: each key drives auto-hide in TemplatePage (no fallback UI shown).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { supabasePublic as supabase } from '../../../config/supabaseClient';
import { fetchJournalForCreator } from '../../../Utils/SupabaseServices/BlogDatabase';
import { getStayUpToDateComponents } from '../../../Utils/HelperFunction/HelperFunction';
import {
  fetchWebsiteTemplateComponentsCached,
  takePrefetchedWebsiteLiveUserRow,
} from './websiteTemplateBootstrap';
import {
  getFirstUsableImageUrlForCard,
  isNavigable,
  pickFirstNonEmptyImageField,
} from '../../../Utils/StayUpToDate/RenderComponent';
import { isShoppable, isContent, CONTENT_SERIES_COMPONENT_TYPE, isAutomatedRecentContentCarousel, isWebsiteProductCollectionCarousel, isCarouselLatestTab, isCarouselSeriesTab } from '../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters';
import { slugifyShopCategory } from './templateShopCatalog';
import { parseProductBreadcrumbs } from './shopBreadcrumbs';
import { parseCollectionDescriptionFromDetails } from '../../../Utils/helpers/carouselHelper';
import { StaticVariables } from '../../../Utils/StaticVariables';
import { buildAboutMoreFromValuesFromTemplateComponents } from './aboutMoreFromSection';
import { syncAboutMoreFromTemplateCardImagesInBackground } from './templateComponentCardImageSync';
import { platformKeyFromComponentRow } from './contentLooks';
import { parseWebsiteLibBannerOptions } from './websiteLibBannerOptions';
import {
  bannerImageFrameCfg,
  readLinkCoverFrame,
} from './templateBannerUtils';
import {
  getWebsiteTemplateDeletedIds,
  getWebsiteTemplateDirtyPatch,
} from './websiteTemplateComponentSession';
import { parseSocialCustomList } from '../../../components/SocialLinks/socialCustomSync';
import type {
  CreatorData,
  CarouselData,
  CarouselItem,
  ContactPageConfig,
  FeaturedCarouselData,
  FeaturedCarouselItem,
  HighlightItem,
  JournalPost,
} from './types';

// ── KNOWN_PLATFORMS whitelist ──────────────────────────────────
// Prevents raw DB keys from appearing as social icons.
// Covers every social_* column observed in the user table (getUserAndCheckExistence).
const KNOWN_PLATFORMS: Record<string, string> = {
  instagram:     'Instagram',
  youtube:       'YouTube',
  tiktok:        'TikTok',
  twitter:       'Twitter',
  x:             'X',
  facebook:      'Facebook',
  pinterest:     'Pinterest',
  linkedin:      'LinkedIn',
  shopmy:        'ShopMy',
  shop:          'Shop',
  ltk:           'LTK',
  amazon:        'Amazon',
  snapchat:      'Snapchat',
  twitch:        'Twitch',
  spotify:       'Spotify',
  applepodcast:  'Apple Podcasts',
  discord:       'Discord',
  website:       'Website',
  newsletter:    'Newsletter',
  podcast:       'Podcast',
};

/** StayUpToDate row fields can be non-strings from JSONB — avoid `[object Object]` in UI. */
function stayuptodateTextField(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function isCampaignComponent(c: { type?: string }): boolean {
  return c.type === 'campaign';
}
function isBannerComponent(c: { type?: string }): boolean {
  const t = (c.type ?? '').trim().toLowerCase();
  // Drawer "Add Banner" writes `custom-banner`; older rows may still be `banner`.
  return t === 'banner' || t === 'custom-banner';
}
function isHighlightCardComponent(c: { type?: string }): boolean {
  return c.type === 'highlight';
}

/**
 * Types that belong to home-page sections (banners, highlights, forms, etc.) and
 * should never appear as carousel tabs on the Content or Shop pages.
 */
const NON_CAROUSEL_COMPONENT_TYPES = new Set([
  'campaign',
  'banner',
  'custom-banner',
  'highlight',
  'highlight-card',
  'links-section',
  'link-section',
  'newsletter-form',
  'newsletter',
  'inline',
  'popup',
  'form',
  'embed-form',
  'embed',
  'carousel-collection',
  'collection-carousel',
]);

function isCarouselComponent(c: {
  type?: string;
  display_name?: string;
  name?: string;
  shoppable_content_filter?: string;
}): boolean {
  const t = (c.type ?? '').trim().toLowerCase();
  if (t && NON_CAROUSEL_COMPONENT_TYPES.has(t)) return false;
  // Always keep MT day-window / trending rows (often stored with empty `type`).
  if (isAutomatedRecentContentCarousel(c)) return true;
  const dn = String(c.display_name ?? '').trim().toLowerCase();
  if (
    dn === 'yesterday' ||
    dn === 'this week' ||
    dn === 'this month' ||
    dn === 'this week content'
  ) {
    return true;
  }
  return Boolean(t);
}

/** Stable row id for deduping stayuptodate_components across template sections. */
function rowIdKey(row: { id?: string | number } | null | undefined): string {
  if (row?.id == null) return '';
  return String(row.id).trim();
}

function isComponentClaimed(
  claimed: Set<string>,
  row: { id?: string | number } | null | undefined,
): boolean {
  const id = rowIdKey(row);
  return id !== '' && claimed.has(id);
}

function claimComponent(
  claimed: Set<string>,
  row: { id?: string | number } | null | undefined,
): boolean {
  const id = rowIdKey(row);
  if (!id || claimed.has(id)) return false;
  claimed.add(id);
  return true;
}

// ── Public types ───────────────────────────────────────────────
export interface HasData {
  bio:        boolean;
  highlights: boolean;
  banners:    boolean;
  /** True when at least one `type === 'highlight'` stayuptodate component exists. */
  highlightCards: boolean;
  links:      boolean;
  carousels:  boolean;
  press:      boolean;
  about:      boolean;
  products:   boolean;
  journal:    boolean;
  recent:     boolean;
  /** True when at least one campaign component exists in StayUpToDate. */
  campaign:   boolean;
  /** True when creator saved a contact form config (drives Contact tab visibility). */
  contact:    boolean;
  /** True when at least one visible `template_components` row exists for the LiB page. */
  linkInBio:  boolean;
}

export interface UseCreatorDataFromDBPreloaded {
  liveData: Partial<CreatorData>;
  hasData:  HasData;
}

export interface UseCreatorDataFromDBOptions {
  /**
   * Parent already fetched live rows (e.g. `MasterTemplateContentProvider` on `/website`).
   * Skips the Supabase fan-out on mount.
   */
  preloaded?: UseCreatorDataFromDBPreloaded;
  /** When false, skip network fetch (public `/website` uses provider as sole source). */
  enabled?: boolean;
  /** Editor-only: include `draft` posts awaiting approval. Public site must omit this. */
  includeDrafts?: boolean;
  /**
   * Collection deep-links (`/shop/{collection}`, `/content/{series}`): skip StayUpToDate
   * + product/IG/feed pools until after first paint so catalog RPCs win the network.
   */
  deferHeavyPools?: boolean;
}

export interface UseCreatorDataFromDBResult {
  liveData: Partial<CreatorData>;
  hasData:  HasData;
  loading:  boolean;
  /**
   * True until the journal/blog fetch settles. Home paint must not wait on this;
   * JournalGrid / PostHero should keep skeletons while it is true and posts are empty.
   */
  journalLoading: boolean;
  /** Latest `user.browsable_template_editing_started_at` (updated on each template save + welcome). */
  browsableTemplateEditingStartedAt: string | null;
  /** After patching `stayuptodate_components.details` image, update in-memory live cards/banners. */
  touchStayComponentDetailImage: (rowId: string, imageUrl: string) => void;
  /**
   * Optimistically patch LiB profile photo fields on live bio.
   * `useMergedCreatorData` prefers live `profilePicture` / `originalProfileUrl`
   * over template_settings, so setCreatorField alone cannot prevent a stale flash.
   */
  touchLibProfilePhoto: (patch: {
    profilePicture?: string;
    originalProfileUrl?: string | null;
    profileCoverFrame?: CreatorData["bio"]["profileCoverFrame"];
  }) => void;
  /** Optimistically patch a Recent Content carousel row in live data (feed controls). */
  patchCarouselLiveFields: (
    rowId: string,
    patch: Partial<CarouselData>,
  ) => void;
  /**
   * Sync Home featured-collection tiles to `memberIds` (add / remove / reorder).
   * Resolves newly added members from existing carousels when possible.
   * Pass `collectionRowId` to update a specific `featuredCollections` entry.
   */
  reorderFeaturedCollection: (
    memberIds: string[],
    collectionRowId?: string,
  ) => void;
  /** Optimistically patch a journal card after approve / unpublish. */
  patchJournalLive: (postId: string, patch: Partial<JournalPost>) => void;
  /** Re-run the full Supabase fetch (e.g. after bootstrapping a default campaign on first template edit). */
  refreshLiveData: () => void;
}

const DEFAULT_HAS_DATA: HasData = {
  bio:        false,
  highlights: false,
  banners:    false,
  highlightCards: false,
  links:      false,
  carousels:  false,
  press:      false,
  // Start true so AboutStory/AboutValues mount immediately and can render their
  // skeleton while the DB fetch is in flight, rather than appearing late.
  // The skeleton detects the empty-about loading state (all fields blank) and
  // transitions to real content once useCreatorDataFromDB resolves.
  about:      true,
  products:   false,
  journal:    false,
  recent:     false,
  campaign:   false,
  contact:    false,
  linkInBio:  false,
};

// ── Raw types ──────────────────────────────────────────────────
interface RawUserRow {
  name?:         string;
  display_name?: string;
  description?:  string;
  /** Plain text, JSON stringified array of strings, or (if JSON column) string[] */
  about_para?:   string | string[];
  /** About-only portrait from cron `/api/about/from-*` (and editor uploads). */
  about_image?:  string;
  picture?:      string;
  storage_url?:  string;
  /** Mirrors `user.browsable_template_editing_started_at` (first open + refreshed on each template save). */
  browsable_template_editing_started_at?: string | null;
  [key: string]: unknown; // covers all social_* columns
}

/** Turns DB `about_para` into display paragraphs (handles JSON `["a","b"]` stored as text). */
function normalizeAboutParaToStory(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return parsed
            .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
            .filter(Boolean);
        }
      } catch {
        /* treat as plain prose */
      }
    }
    return [t];
  }
  return [];
}

interface RawLinkRow {
  id:               number;
  link_name?:       string;
  link_url?:        string;
  image_url?:       string;
  collection_name?: string;
  parent_id?:       string | null;
  type?:            string;
  index?:           number;
}

interface RawProductRow {
  product_id?: string | number;
  name?:       string;
  url?:        string;
  image?:      string;
  query?:      string;
}

interface RawContentRow {
  post_id?:       string | number;
  caption?:       string;
  storage_url?:   string;
  thumbnail_url?: string;
  post_date?:     string;
  type?:          string;
}

type RawContactPageRow = {
  field_config?: { fields?: Record<string, boolean> | null } | null;
  image_url?: string | null;
  creator_mail?: string | null;
  title_contact_text?: string | null;
  button_contact_text?: string | null;
} | null;

/**
 * Map `contact_page` (+ account-email fallback) into CreatorData.contact.
 * Kept pure so Contact can apply as soon as its query settles without waiting
 * on StayUpToDate / links in Phase 1b.
 */
function mapContactPageConfig(
  contactRow: RawContactPageRow,
  userRow: RawUserRow | undefined,
): ContactPageConfig | null {
  const contactFields = contactRow?.field_config?.fields
    ? Object.keys(contactRow.field_config.fields).filter(
        (k) => contactRow!.field_config!.fields![k] === true,
      )
    : [];
  const contactMail = String(contactRow?.creator_mail ?? "").trim();
  const accountEmail = String(
    (userRow as { email?: string | null } | undefined)?.email ?? "",
  ).trim();
  // Honour explicit hide flag; otherwise rely on row presence + valid fields.
  const showContactPage = userRow?.show_contact_page !== false;
  const contactEnabled =
    showContactPage &&
    contactMail.length > 0 &&
    contactFields.includes("Email") &&
    contactFields.includes("Message");

  let contact: ContactPageConfig | null = contactRow
    ? {
        enabled: contactEnabled,
        fields: contactFields,
        creatorMail: contactMail,
        titleText: String(contactRow.title_contact_text ?? "").trim(),
        buttonText: String(contactRow.button_contact_text ?? "").trim(),
        imageUrl: String(contactRow.image_url ?? "").trim(),
      }
    : null;

  if (!contact?.enabled && accountEmail) {
    contact = {
      emailFallback: true,
      enabled: true,
      fields: ["Name", "Email", "Message"],
      creatorMail: accountEmail,
      titleText: "Get in touch",
      buttonText: "Send message",
      imageUrl: "",
    };
  }

  return contact;
}

// ── Highlights helpers ─────────────────────────────────────────
// Mirrors `FeedNew.tsx` → `processContentFilters` so the Highlights strip on
// the homepage surfaces the same platform tabs the dashboard feed exposes.

/** Display labels for highlight platform keys (same wording as Feed filters). */
const HIGHLIGHT_LABELS: Record<string, string> = {
  instagram_posts:   'Instagram Posts',
  instagram_stories: 'Instagram Stories',
  youtube:           'YouTube',
  tiktok:            'TikTok',
  pinterest:         'Pinterest',
  newsletter:        'Newsletter',
  twitter:           'X',
  podcast:           'Podcast',
  linkedin:          'LinkedIn',
  facebook:          'Facebook',
  snapchat:          'Snapchat',
  spotify:           'Spotify',
  applepodcast:      'Apple Podcast',
  discord:           'Discord',
};

interface FeedFilterPost { platform?: string; type?: string; post_id?: string }

/** Same instagram split + lowercasing as `FeedNew.processContentFilters`. */
function postToHighlightPlatformKey(post: FeedFilterPost): string | null {
  const platform = String(post?.platform ?? '').trim();
  const type = String(post?.type ?? '').toUpperCase();
  if (!platform) return null;
  if (platform === 'instagram') {
    if (['FEED', 'REELS'].includes(type)) return 'instagram_posts';
    if (['STORY', 'HIGHLIGHTS'].includes(type)) return 'instagram_stories';
    return null;
  }
  if (platform === 'MISC-content') return null;
  // LTK/Amazon/ShopMy/MISC are excluded — same as FeedNew.filterShowSocials.
  if (['MISC', 'LTK', 'Amazon', 'ShopMy'].includes(platform)) return null;
  return platform.toLowerCase();
}

type LatestStoryRow = {
  post_id?: string | number;
  storage_url?: string;
  thumbnail_url?: string;
  caption?: string;
} | null;

function buildHighlightsFromFeed(
  feedFiltersRes: { data?: { posts?: FeedFilterPost[] }[] } | null,
  latestStoryRes: LatestStoryRow,
): HighlightItem[] {
  const feedFiltersRow = feedFiltersRes?.data?.[0];
  const feedPosts: FeedFilterPost[] = Array.isArray(feedFiltersRow?.posts)
    ? feedFiltersRow!.posts!
    : [];

  const platformKeysPresent = new Set<string>();
  const platformPostIds = new Map<string, string[]>();
  const platformPostIdSeen = new Map<string, Set<string>>();
  for (const p of feedPosts) {
    const key = postToHighlightPlatformKey(p);
    const postId = String(p?.post_id ?? '').trim();
    if (!key) continue;
    platformKeysPresent.add(key);
    if (!postId) continue;
    let ids = platformPostIds.get(key);
    let seen = platformPostIdSeen.get(key);
    if (!ids) {
      ids = [];
      seen = new Set();
      platformPostIds.set(key, ids);
      platformPostIdSeen.set(key, seen!);
    }
    if (seen!.has(postId)) continue;
    seen!.add(postId);
    ids.push(postId);
  }

  const latestStoryCover = String(
    latestStoryRes?.thumbnail_url || latestStoryRes?.storage_url || '',
  ).trim();
  const latestStoryPostId = String(latestStoryRes?.post_id ?? '').trim();
  if (latestStoryPostId) {
    let ids = platformPostIds.get('instagram_stories');
    let seen = platformPostIdSeen.get('instagram_stories');
    if (!ids) {
      ids = [];
      seen = new Set();
      platformPostIds.set('instagram_stories', ids);
      platformPostIdSeen.set('instagram_stories', seen);
    }
    if (!seen!.has(latestStoryPostId)) {
      seen!.add(latestStoryPostId);
      ids.unshift(latestStoryPostId);
    }
  }

  const highlights: HighlightItem[] = [];
  if (platformKeysPresent.has('instagram_stories') || latestStoryCover) {
    highlights.push({
      id: 'instagram_stories',
      label: HIGHLIGHT_LABELS.instagram_stories,
      cover: latestStoryCover,
      iconKey: 'instagram_stories',
      postIds: platformPostIds.get('instagram_stories') ?? [],
    });
    platformKeysPresent.delete('instagram_stories');
  }

  const remainingPlatformKeys = Array.from(platformKeysPresent).sort((a, b) =>
    (HIGHLIGHT_LABELS[a] ?? a).localeCompare(HIGHLIGHT_LABELS[b] ?? b),
  );
  for (const key of remainingPlatformKeys) {
    if (key === 'MISC-content') continue;
    highlights.push({
      id: key,
      label: HIGHLIGHT_LABELS[key] ?? key,
      cover: '',
      iconKey: key,
      postIds: platformPostIds.get(key) ?? [],
    });
  }
  return highlights;
}

function mapProductBankRows(rawProducts: RawProductRow[]): NonNullable<CreatorData['products']> {
  return rawProducts.map((p, i) => ({
    id:       String(p.product_id ?? i),
    name:     p.name  ?? '',
    // Primary department for nav / Minimal "Top categories" (not the full crumb path).
    category: parseProductBreadcrumbs(p.query)?.primary ?? '',
    price:    '',
    image:    p.image ?? '',
  }));
}

function mapContentItemPool(rawContent: RawContentRow[]): CarouselItem[] {
  return rawContent
    .filter((c) => c.storage_url || c.thumbnail_url)
    .map((c, i) => ({
      id:       String(c.post_id ?? i),
      title:    (c.caption ?? '').split('\n')[0]?.slice(0, 60) || '',
      subtitle: c.type === 'VIDEO' ? 'Video' : 'Post',
      image:    c.storage_url || c.thumbnail_url || '',
      url:      '#',
    }));
}

function mapShopItemPool(rawProducts: RawProductRow[]): CarouselItem[] {
  return rawProducts.map((p, i) => ({
    id:       String(p.product_id ?? i),
    title:    p.name ?? '',
    subtitle: (p.query ?? '').replace(/\|/g, '').trim(),
    image:    p.image ?? '',
    url:      p.url ?? '#',
  }));
}

type CampaignFieldRow = { label: string; required?: boolean; system?: boolean };

function applyCreatorsCampaignRow(
  campaignRow: {
    campaign_name?: unknown;
    campaign_button_text?: unknown;
    metadata?: unknown;
  } | null | undefined,
  current: {
    campaignName: string;
    campaignButtonText: string;
    campaignDescription: string;
    campaignFields: CampaignFieldRow[];
  },
): typeof current {
  if (!campaignRow) return current;
  let { campaignName, campaignButtonText, campaignDescription, campaignFields } = current;
  const liveName = String(campaignRow.campaign_name ?? '').trim();
  if (liveName) campaignName = liveName;
  const liveButton = String(campaignRow.campaign_button_text ?? '').trim();
  if (liveButton) campaignButtonText = liveButton;
  const meta =
    campaignRow.metadata &&
    typeof campaignRow.metadata === 'object' &&
    !Array.isArray(campaignRow.metadata)
      ? (campaignRow.metadata as { description?: unknown; fields?: unknown })
      : null;
  const liveDesc = String(meta?.description ?? '').trim();
  if (liveDesc) campaignDescription = liveDesc;
  const metaFields = meta?.fields;
  if (Array.isArray(metaFields) && metaFields.length > 0) {
    campaignFields = (metaFields as unknown[])
      .map((f) => {
        if (!f || typeof f !== 'object') return null;
        const row = f as { label?: unknown; required?: unknown; system?: unknown };
        const label = String(row.label ?? '').trim();
        if (!label) return null;
        return {
          label,
          required: Boolean(row.required),
          system: Boolean(row.system),
        };
      })
      .filter(Boolean) as CampaignFieldRow[];
  }
  return { campaignName, campaignButtonText, campaignDescription, campaignFields };
}

// ── Hook ───────────────────────────────────────────────────────
export function useCreatorDataFromDB(
  userId:   string | undefined,
  username: string | undefined,
  options?: UseCreatorDataFromDBOptions,
): UseCreatorDataFromDBResult {
  const preloaded = options?.preloaded;
  const enabled = options?.enabled !== false;
  const includeDrafts = options?.includeDrafts === true;
  const deferHeavyPools = options?.deferHeavyPools === true;
  const skipFetch = Boolean(preloaded && userId?.trim());

  const [liveData, setLiveData] = useState<Partial<CreatorData>>(
    () => (skipFetch ? preloaded!.liveData : {}),
  );
  const [hasData,  setHasData]  = useState<HasData>(
    () => (skipFetch ? preloaded!.hasData : DEFAULT_HAS_DATA),
  );
  /** Start true when `userId` is set so the template shell never flashes StayUpToDate loaders before the bootstrap loader. */
  const [loading,  setLoading]  = useState(() =>
    skipFetch ? false : Boolean(userId?.trim()),
  );
  /** Journal/blog fetch is independent of home first paint. */
  const [journalLoading, setJournalLoading] = useState(() =>
    skipFetch ? false : Boolean(userId?.trim() && enabled),
  );
  /** Avoid wiping the dashboard preview when `username` hydrates after `userId`. */
  const liveDataLoadedForUserRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (skipFetch) {
      setLiveData(preloaded!.liveData);
      setHasData(preloaded!.hasData);
      setLoading(false);
      setJournalLoading(false);
      liveDataLoadedForUserRef.current = userId?.trim() || null;
      return;
    }
    if (!userId?.trim()) {
      liveDataLoadedForUserRef.current = null;
      setJournalLoading(false);
      setLoading(false);
      return;
    }
    if (liveDataLoadedForUserRef.current !== userId.trim()) {
      setLoading(true);
      setJournalLoading(true);
    }
  }, [userId, skipFetch, preloaded]);
  const [browsableTemplateEditingStartedAt, setBrowsableTemplateEditingStartedAt] =
    useState<string | null>(null);
  const [liveDataRefreshNonce, setLiveDataRefreshNonce] = useState(0);

  const refreshLiveData = useCallback(() => {
    setLiveDataRefreshNonce((n) => n + 1);
  }, []);

  const touchStayComponentDetailImage = useCallback((rowId: string, imageUrl: string) => {
    const url = imageUrl.trim();
    const rid = rowId.trim();
    if (!url || !rid) return;
    setLiveData((prev) => {
      const next: Partial<CreatorData> = { ...prev };
      if (prev.banners) {
        const b = { ...prev.banners };
        let hit = false;
        for (const k of ['hero', 'promo', 'footer'] as const) {
          if (b[k]?.stayComponentRowId === rid) {
            b[k] = { ...b[k]!, image: url };
            hit = true;
          }
        }
        if (b.catalog?.length) {
          b.catalog = b.catalog.map((item) => {
            if (String(item?.stayComponentRowId || item?.id || '') !== rid) {
              return item;
            }
            hit = true;
            return { ...item, image: url };
          });
        }
        if (hit) next.banners = b;
      }
      if (prev.highlightCards?.length) {
        let hit = false;
        const cards = prev.highlightCards.map((c) => {
          if (c.stayComponentRowId === rid) {
            hit = true;
            return { ...c, image: url };
          }
          return c;
        });
        if (hit) next.highlightCards = cards;
      }
      return next;
    });
  }, []);

  const touchLibProfilePhoto = useCallback(
    (patch: {
      profilePicture?: string;
      originalProfileUrl?: string | null;
      profileCoverFrame?: CreatorData["bio"]["profileCoverFrame"];
    }) => {
      if (!patch || Object.keys(patch).length === 0) return;
      setLiveData((prev) => {
        if (!prev.bio) return prev;
        const nextBio: CreatorData["bio"] = { ...prev.bio };
        if ("profilePicture" in patch && patch.profilePicture != null) {
          const url = String(patch.profilePicture || "").trim();
          nextBio.profilePicture = url;
          // Shared default until Hero / About override their own slots.
          nextBio.avatar = url || nextBio.avatar;
        }
        if ("originalProfileUrl" in patch) {
          const original = String(patch.originalProfileUrl || "").trim();
          nextBio.originalProfileUrl = original || undefined;
        }
        if ("profileCoverFrame" in patch) {
          nextBio.profileCoverFrame = patch.profileCoverFrame ?? null;
        }
        return { ...prev, bio: nextBio };
      });
    },
    [],
  );

  const patchCarouselLiveFields = useCallback(
    (rowId: string, patch: Partial<CarouselData>) => {
      const rid = String(rowId || "").trim();
      if (!rid || !patch || Object.keys(patch).length === 0) return;
      setLiveData((prev) => {
        if (!prev.carousels?.length) return prev;
        let hit = false;
        const carousels = prev.carousels.map((c) => {
          if (c.stayComponentRowId !== rid) return c;
          hit = true;
          return { ...c, ...patch };
        });
        return hit ? { ...prev, carousels } : prev;
      });
    },
    [],
  );

  const reorderFeaturedCollection = useCallback(
    (memberIds: string[], collectionRowId?: string) => {
      const order = memberIds
        .map((id) => String(id || "").trim())
        .filter(Boolean);
      const collectionKey = String(collectionRowId || "").trim();
      setLiveData((prev) => {
        const byId = new Map<string, FeaturedCarouselItem>();

        const seed = (item: FeaturedCarouselItem | null | undefined) => {
          if (!item) return;
          const id = String(item.stayComponentRowId || item.id || "").trim();
          if (id && !byId.has(id)) byId.set(id, item);
        };

        for (const item of prev.featuredCarousel?.items || []) seed(item);
        for (const col of prev.featuredCollections || []) {
          for (const item of col.items || []) seed(item);
        }
        for (const c of prev.carousels || []) {
          const id = String(c.stayComponentRowId || "").trim();
          if (!id || byId.has(id)) continue;
          byId.set(id, {
            id,
            title: String(c.title || id),
            image:
              String(
                c.coverImage ||
                  c.items?.[0]?.image ||
                  "",
              ).trim() || "",
            slug: String(c.renderSlug || c.id || id),
            kind: c.type === "shop" ? "shop" : "content",
            stayComponentRowId: id,
          });
        }

        const nextItems = order
          .map((id) => {
            const existing = byId.get(id);
            if (existing) return existing;
            return {
              id,
              title: id,
              image: "",
              slug: id,
              kind: "content" as const,
              stayComponentRowId: id,
            };
          })
          .filter(Boolean) as FeaturedCarouselItem[];

        let featuredCarousel = prev.featuredCarousel;
        if (
          featuredCarousel &&
          featuredCarousel.mode === "collection" &&
          (!collectionKey ||
            String(featuredCarousel.stayComponentRowId || "").trim() ===
              collectionKey)
        ) {
          const same =
            nextItems.length === featuredCarousel.items.length &&
            nextItems.every(
              (item, index) =>
                String(item.stayComponentRowId || item.id) ===
                String(
                  featuredCarousel!.items[index]?.stayComponentRowId ||
                    featuredCarousel!.items[index]?.id,
                ),
            );
          if (!same) {
            featuredCarousel = { ...featuredCarousel, items: nextItems };
          }
        }

        let featuredCollections = prev.featuredCollections;
        if (collectionKey && Array.isArray(featuredCollections)) {
          let changed = false;
          featuredCollections = featuredCollections.map((col) => {
            if (String(col.stayComponentRowId || "").trim() !== collectionKey) {
              return col;
            }
            changed = true;
            return { ...col, items: nextItems };
          });
          if (!changed) featuredCollections = prev.featuredCollections;
        }

        if (
          featuredCarousel === prev.featuredCarousel &&
          featuredCollections === prev.featuredCollections
        ) {
          return prev;
        }
        return {
          ...prev,
          featuredCarousel,
          ...(featuredCollections
            ? { featuredCollections }
            : {}),
        };
      });
    },
    [],
  );

  const patchJournalLive = useCallback(
    (postId: string, patch: Partial<JournalPost>) => {
      const id = String(postId || "").trim();
      if (!id || !patch || Object.keys(patch).length === 0) return;
      setLiveData((prev) => {
        if (!prev.journal?.length) return prev;
        let hit = false;
        const journal = prev.journal.map((p) => {
          if (String(p.id) !== id) return p;
          hit = true;
          return { ...p, ...patch };
        });
        return hit ? { ...prev, journal } : prev;
      });
    },
    [],
  );

  /** Public handles are case-insensitive in URLs; keep template + API calls normalized. */
  const usernameLc = username?.trim().toLowerCase() || undefined;

  useEffect(() => {
    const uid = userId?.trim() || "";
    if (!enabled) {
      return;
    }
    if (!uid) {
      liveDataLoadedForUserRef.current = null;
      setLiveData({});
      setHasData(DEFAULT_HAS_DATA);
      setLoading(false);
      setJournalLoading(false);
      setBrowsableTemplateEditingStartedAt(null);
      return;
    }

    if (skipFetch && liveDataRefreshNonce === 0) {
      return;
    }

    /** Background refresh — keep preview mounted (username hydrate, campaign bootstrap, manual refresh). */
    const isBackgroundRefresh =
      liveDataRefreshNonce > 0 || liveDataLoadedForUserRef.current === uid;

    if (!isBackgroundRefresh) {
      liveDataLoadedForUserRef.current = null;
      setLiveData({});
      setHasData(DEFAULT_HAS_DATA);
      setBrowsableTemplateEditingStartedAt(null);
    }

    let cancelled = false;

    async function fetchAll() {
      if (!isBackgroundRefresh) setLoading(true);
      // Keep Blog on skeletons until journal settles — do not clear this in the
      // outer catch (phase-2 errors used to flash "No blog posts" first).
      setJournalLoading(true);
      let journal: CreatorData['journal'] = [];
      let journalPromiseStarted = false;
      const totalT0 = performance.now();
      const timed = <T,>(label: string, p: PromiseLike<T>): Promise<T> => {
        const t0 = performance.now();
        return Promise.resolve(p).then(
          (value) => {
        
            return value;
          },
          (err) => {
            
            throw err;
          },
        );
      };
    
      try {
        // ── Phase 1a: user + template_components (+ overlap links/contact) ─
        // Bootstrap often already warmed template_components — reuse that cache.
        // Start user_links + contact_page in the same window so phase 1b rarely
        // waits another Slow 4G RTT after unlock. StayUpToDate / product pools
        // still wait until after unlock (and may be deferred entirely).
        const prefetchedUser = isBackgroundRefresh
          ? null
          : takePrefetchedWebsiteLiveUserRow(uid);
        const userPromise = timed(
          "user.select(*)",
          prefetchedUser
            ? prefetchedUser.catch(() =>
                supabase.from("user").select("*").eq("user_id", userId).limit(1),
              )
            : supabase.from("user").select("*").eq("user_id", userId).limit(1),
        );

        const carouselPromise = timed(
          "template_components",
          usernameLc
            ? fetchWebsiteTemplateComponentsCached(usernameLc, userId!)
            : Promise.resolve({
                order: [],
                data: [],
                showingComponents: [],
                archiveComponents: [],
              }),
        );

        // One user_links round-trip for website Top Links + LiB empty-state enrich.
        const linksPromise = timed(
          "user_links (website-template + landing-page)",
          supabase
            .from('user_links')
            .select('id,link_name,link_url,image_url,collection_name,parent_id,type,index')
            .eq('user_id', userId)
            .eq('status', 'active')
            .in('type', ['website-template', 'landing-page'])
            .order('index', { ascending: true })
            .limit(400),
        );

        const contactPromise = timed(
          "contact_page",
          supabase
            .from('contact_page')
            .select(
              'field_config, image_url, creator_mail, title_contact_text, button_contact_text',
            )
            .eq('user_id', userId)
            .maybeSingle(),
        );

        // Phase 1a: user + template_components together (unchanged unlock timing —
        // do not clear loading early; that would change the public loader / skeletons).
        const [userRes, carouselRes] = (await Promise.all([
          userPromise,
          carouselPromise,
        ])) as [
          { data: RawUserRow[] | null },
          Awaited<ReturnType<typeof fetchWebsiteTemplateComponentsCached>>,
        ];

        if (cancelled) return;

        // ── Map: User ────────────────────────────────────────────────
        const userRow = (userRes.data?.[0] ?? undefined) as RawUserRow | undefined;

        const displayName = userRow?.name?.trim() || userRow?.display_name?.trim() || '';

        // Blog fetch starts after phase-1a unlock (see journalPromise below) so it
        // does not contend with Bio/nav or featured-strip RPCs on first paint.

        const btRaw = userRow?.browsable_template_editing_started_at;
        const btStarted =
          btRaw != null && String(btRaw).trim() !== ''
            ? String(btRaw)
            : null;
        setBrowsableTemplateEditingStartedAt(btStarted);

        // Build social links in the same order as Social links (`social_links_order`).
        // Custom rows use their saved id + name — never a generic "Link".
        const social: { platform: string; url: string }[] = [];
        if (userRow) {
          const orderRaw = typeof userRow.social_links_order === 'string'
            ? userRow.social_links_order.split(',').map((v: string) => v.trim()).filter(Boolean)
            : [];

          const byKey = new Map<string, { platform: string; url: string }>();
          Object.entries(userRow).forEach(([key, value]) => {
            if (
              !key.startsWith('social_') ||
              key === 'social_links_order' ||
              key === 'social_custom'
            ) {
              return;
            }
            if (typeof value !== 'string' || !value.trim()) return;
            const platformKey = key.replace('social_', '').toLowerCase();
            const label = KNOWN_PLATFORMS[platformKey];
            if (!label) return;
            byKey.set(platformKey, { platform: label, url: value.trim() });
          });

          for (const entry of parseSocialCustomList(userRow.social_custom)) {
            const url = String(entry.link || '').trim();
            if (!url) continue;
            const id = (String(entry.id || '').trim() || `custom-${url}`).toLowerCase();
            const label = String(entry.name || '').trim() || 'Link';
            byKey.set(id, { platform: label, url });
          }

          if (orderRaw.length === 0) {
            byKey.forEach((row) => social.push(row));
          } else {
            const used = new Set<string>();
            for (const rawKey of orderRaw) {
              const key = rawKey.toLowerCase();
              const hit = byKey.get(key);
              if (!hit) continue;
              const dedupe = `${hit.platform}::${hit.url}`.toLowerCase();
              if (used.has(dedupe)) continue;
              used.add(dedupe);
              used.add(key);
              social.push(hit);
            }
            byKey.forEach((row, key) => {
              const dedupe = `${row.platform}::${row.url}`.toLowerCase();
              if (used.has(dedupe) || used.has(key)) return;
              used.add(dedupe);
              social.push(row);
            });
          }
        }

        // Same connection fields as Connected Platforms (`IntegrationsBox.handleConnected`).
        const connectedPlatforms: NonNullable<
          CreatorData['bio']['connectedPlatforms']
        > = {
          Instagram: Boolean(userRow?.instagram_username),
          YouTube: Boolean(userRow?.youtube_channel_id),
          TikTok: Boolean(userRow?.tiktok_user),
          X: Boolean(userRow?.twitter_user),
          LinkedIn: Boolean(userRow?.linkedin_user),
          Podcast: Boolean(userRow?.podcast_link),
        };

        // Shared default for Hero / About / LiB until each surface has its own override.
        // Prefer `picture` first — same field the dashboard sidebar uses.
        // `storage_url` can be stale/expired while Auth0 `picture` still loads.
        const profilePicture = String(
          userRow?.picture || userRow?.storage_url || "",
        ).trim();
        const originalProfileUrl = String(
          userRow?.original_profile_url || "",
        ).trim();
        const coverFrame = readLinkCoverFrame(userRow?.image_crop);
        const profileCoverFrame = coverFrame
          ? bannerImageFrameCfg(coverFrame)
          : null;

        const bio: CreatorData['bio'] = {
          name:        userRow?.display_name?.trim() || '',
          displayName: userRow?.display_name?.trim() || '',
          handle:      usernameLc ? `@${usernameLc}` : '',
          // Hero default; `template_settings.bio.avatar` overrides in merge.
          avatar:      profilePicture,
          // LiB always tracks the live user profile columns.
          profilePicture,
          originalProfileUrl: originalProfileUrl || undefined,
          profileCoverFrame,
          tagline:     userRow?.description ?? '',
          sublabel:    '',
          location:    '',   // removed per mapping
          cta:         '',   // removed per mapping
          cta2:        '',
          social,
          connectedPlatforms,
          // Expose to section components so they can call live data hooks per-carousel tab
          username:    usernameLc,
          userId,
        };

        // Highlights / products fill in phase 2 (feed RPC + product bank).
        const highlights: HighlightItem[] = [];
        const products: CreatorData['products'] = [];

        // Links need stayuptodate (top links-section) + user_links — filled in phase 1b.
        let rawLinks: RawLinkRow[] = [];
        let links: CreatorData['links'] = [];
        let linksTitle = '';
        let topLinksCollection = '';
        let topLinksSectionId = '';

        // ── Map: template_components → carousels (order = getTemplateComponents / Supabase `index`) ─────
        const showingComponents: {
          id?: string | number;
          name?: string;
          display_name?: string;
          type?: string;
          cta_placeholder?: string;
          details?: string;
          post_ids?: unknown;
          content_order?: string;
          custom_order?: string;
          status?: string;
          status_expiry?: string;
        }[] = (() => {
          const live = (carouselRes.showingComponents ?? []) as {
            id?: string | number;
            name?: string;
            display_name?: string;
            type?: string;
            cta_placeholder?: string;
            details?: string;
            post_ids?: unknown;
            content_order?: string;
            custom_order?: string;
            status?: string;
            status_expiry?: string;
          }[];
          if (!includeDrafts) return live;
          const draftRows = (
            (carouselRes.archiveComponents ?? []) as typeof live
          ).filter((row) => {
            const st = String(row.status_expiry ?? row.status ?? "")
              .toLowerCase()
              .trim();
            return st === "draft";
          });
          if (!draftRows.length) return live;
          const seen = new Set(
            live.map((r) => String(r.id || "").trim()).filter(Boolean),
          );
          const merged = [...live];
          for (const row of draftRows) {
            const id = String(row.id || "").trim();
            if (id && seen.has(id)) continue;
            if (id) seen.add(id);
            merged.push(row);
          }
          return merged;
        })().filter((row) => {
          const id = String(row.id || "").trim();
          if (!id) return true;
          return !getWebsiteTemplateDeletedIds().includes(id);
        });

        // Each stayuptodate row may back at most one template section (homepage order).
        const claimedComponents = new Set<string>();

        const emailCampaignsEnabled = Boolean(userRow?.active_campaign);
        const flodeskPopupFormId = (() => {
          if (!userRow?.is_flowdeskpopup_on) return undefined;
          const id = String(userRow?.flowdeskpopup_user || "").trim();
          return id || undefined;
        })();
        const campaignRows = showingComponents.filter((c) =>
          isCampaignComponent(c),
        );
        for (const row of campaignRows) claimComponent(claimedComponents, row);
        const campaignBlocks = emailCampaignsEnabled
          ? campaignRows
              .map((row) => {
                const raw = row as {
                  id?: unknown;
                  name?: string;
                  display_name?: string;
                  details?: unknown;
                  user_id?: string;
                };
                const id = String(raw.id ?? "").trim();
                if (!id) return null;
                const pending = includeDrafts
                  ? getWebsiteTemplateDirtyPatch(id)
                  : undefined;
                const details = pending?.details ?? raw.details;
                let detailsName = "";
                if (details && typeof details === "object" && !Array.isArray(details)) {
                  detailsName = String(
                    (details as { campaign_name?: unknown }).campaign_name || "",
                  ).trim();
                } else if (typeof details === "string" && details.trim()) {
                  try {
                    const parsed = JSON.parse(details) as { campaign_name?: unknown };
                    detailsName = String(parsed?.campaign_name || "").trim();
                  } catch {
                    detailsName = "";
                  }
                }
                const pendingName = String(
                  pending?.display_name || pending?.name || "",
                ).trim();
                return {
                  id,
                  name:
                    pendingName ||
                    detailsName ||
                    String(raw.display_name || "").trim() ||
                    String(raw.name || "").trim(),
                  details,
                  user_id: raw.user_id,
                };
              })
              .filter(
                (
                  row,
                ): row is {
                  id: string;
                  name?: string;
                  details?: unknown;
                  user_id?: string;
                } => row != null,
              )
          : [];
        const campaignComp = emailCampaignsEnabled ? campaignRows[0] : undefined;
        const campaignRaw = campaignComp as
          | {
              route_name?: string;
              unique_id?: string;
              name?: string;
              details?: string | Record<string, unknown> | null;
            }
          | undefined;
        const campaignRenderSlug = campaignRaw
          ? String(
              (campaignRaw.route_name &&
                String(campaignRaw.route_name).trim()) ||
                (campaignRaw.unique_id &&
                  String(campaignRaw.unique_id).trim()) ||
                (campaignRaw.name && String(campaignRaw.name).trim()) ||
                '',
            ).trim()
          : '';
        let campaignId = '';
        let campaignName = '';
        let campaignDescription = '';
        let campaignButtonText = 'Subscribe';
        let campaignFields: {
          label: string;
          required?: boolean;
          system?: boolean;
        }[] = [];
        if (campaignRaw) {
          let detailsObj: Record<string, unknown> = {};
          const rawDetails = campaignRaw.details;
          if (rawDetails && typeof rawDetails === 'object' && !Array.isArray(rawDetails)) {
            detailsObj = rawDetails as Record<string, unknown>;
          } else if (typeof rawDetails === 'string' && rawDetails.trim()) {
            try {
              const parsed = JSON.parse(rawDetails);
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                detailsObj = parsed as Record<string, unknown>;
              }
            } catch {
              detailsObj = {};
            }
          }
          campaignId = String(detailsObj.campaign_id ?? '').trim();
          campaignName = String(
            detailsObj.campaign_name ?? campaignRaw.name ?? '',
          ).trim();
          campaignDescription = String(detailsObj.description ?? '').trim();
          campaignButtonText = String(
            detailsObj.campaign_button_text ?? 'Subscribe',
          ).trim() || 'Subscribe';
          if (Array.isArray(detailsObj.fields)) {
            campaignFields = (detailsObj.fields as unknown[])
              .map((f) => {
                if (!f || typeof f !== 'object') return null;
                const row = f as { label?: unknown; required?: unknown; system?: unknown };
                const label = String(row.label ?? '').trim();
                if (!label) return null;
                return {
                  label,
                  required: Boolean(row.required),
                  system: Boolean(row.system),
                };
              })
              .filter(Boolean) as {
              label: string;
              required?: boolean;
              system?: boolean;
            }[];
          }

          // Live `creators_campaigns` row is merged in phase 2 so it cannot
          // block first paint.
        }

        // Claim the top links-section so banners/featured don't reuse it.
        // Actual `user_links` rows arrive in phase 1b with stayuptodate.
        const topLinksSection = showingComponents.find(
          (c) =>
            (c.type ?? '').trim() === 'links-section' &&
            !isComponentClaimed(claimedComponents, c),
        );
        if (topLinksSection) claimComponent(claimedComponents, topLinksSection);
        topLinksCollection = topLinksSection?.name
          ? String(topLinksSection.name).trim()
          : '';
        topLinksSectionId = topLinksSection?.id
          ? String(topLinksSection.id).trim()
          : '';
        linksTitle = topLinksSection
          ? String(topLinksSection.display_name ?? topLinksSection.name ?? '').trim()
          : '';

        // ── Map: Banner components → liveData.banners (one row per slot, no reuse) ─
        const bannerItems: {
          id: string;
          title: string;
          subtitle: string;
          cta: string;
          url: string;
          image: string;
          stayComponentRowId?: string;
        }[] = [];
        for (const comp of showingComponents) {
          if (!isBannerComponent(comp) || isComponentClaimed(claimedComponents, comp)) {
            continue;
          }
          if (!claimComponent(claimedComponents, comp)) continue;
          // Unpublished image (deferred until Publish) must survive refetch —
          // same overlay highlights use for `details`.
          const pending = getWebsiteTemplateDirtyPatch(comp.id);
          const pendingDetails = pending?.details;
          const detailsRaw =
            typeof pendingDetails === 'string'
              ? pendingDetails
              : comp.details ?? '';
          const parts = detailsRaw.split(',');
          const fromDetails = parts[0]?.trim() ?? '';
          const pendingOriginal = pending?.original_banner_image;
          const originalBanner = String(
            (typeof pendingOriginal === 'string' ? pendingOriginal : undefined) ??
              (comp as { original_banner_image?: unknown }).original_banner_image ??
              '',
          ).trim();
          const imageUrl =
            fromDetails.startsWith('http') || fromDetails.startsWith('/')
              ? fromDetails
              : originalBanner || fromDetails;
          const ctaUrl = parts[1]?.trim() ?? '#';
          const bannerOpts = parseWebsiteLibBannerOptions(
            (comp as { query?: unknown }).query,
          );
          bannerItems.push({
            id: String(comp.id ?? bannerItems.length),
            title:
              stayuptodateTextField(comp.display_name) ||
              stayuptodateTextField(comp.name),
            subtitle: bannerOpts.subtitle,
            cta: comp.cta_placeholder ?? 'Learn More',
            url: ctaUrl,
            image: imageUrl,
            imageZoom: bannerOpts.imageZoom,
            imageX: bannerOpts.imageX,
            imageY: bannerOpts.imageY,
            stayComponentRowId:
              comp.id != null && String(comp.id).trim() !== ''
                ? String(comp.id)
                : undefined,
          });
        }
        const liveBanners =
          bannerItems.length > 0
            ? {
                hero: bannerItems[0],
                promo: bannerItems[1],
                footer: bannerItems[2],
                catalog: bannerItems,
              }
            : undefined;

        // ── Map: Highlight components → liveData.highlightCards ─────
        const highlightCards: CreatorData['highlightCards'] = [];
        for (const comp of showingComponents) {
          if (
            !isHighlightCardComponent(comp) ||
            isComponentClaimed(claimedComponents, comp)
          ) {
            continue;
          }
          if (!claimComponent(claimedComponents, comp)) continue;
          // Unpublished image/link (deferred until Publish) must survive refetch.
          const pendingDetails = getWebsiteTemplateDirtyPatch(comp.id)?.details;
          const parts = (
            typeof pendingDetails === 'string' ? pendingDetails : comp.details ?? ''
          ).split(',');
          const imageUrl = parts[0]?.trim() ?? '';
          const ctaUrl = parts[1]?.trim() ?? '#';
          highlightCards.push({
            id: String(comp.id ?? highlightCards.length),
            title:
              stayuptodateTextField(comp.name) ||
              stayuptodateTextField(comp.display_name),
            subtitle: '',
            cta: comp.cta_placeholder ?? 'Learn More',
            url: ctaUrl,
            image: imageUrl,
            stayComponentRowId:
              comp.id != null && String(comp.id).trim() !== ''
                ? String(comp.id)
                : undefined,
          });
        }

        // Carousel item pools fill in phase 2 (content_instagram + product_bank).
        const contentItemPool: CarouselItem[] = [];
        const shopItemPool: CarouselItem[] = [];

        // Map to CarouselData[] — include all carousel-like components:
        //   • shoppable  → Shop page
        //   • content-series → Recent Content page, Series tab
        //   • everything else that isn't a home-page section component → Recent Content page, Latest tab
        // postIds/componentType/contentOrder/customOrder are stored so section components
        // can call the same hooks the link-in-bio uses (useLoadCustomContent, useLoadCustomProduct).
        const carousels: CarouselData[] = showingComponents
          .filter((c) => {
            if (!isCarouselComponent(c)) return false;
            const tabShape = {
              componentType: c.type,
              componentDisplayName: String(c.display_name ?? '').trim() || undefined,
              name: String(c.name ?? ''),
              title: stayuptodateTextField(c.name),
              shoppableContentFilter: String(
                (c as { shoppable_content_filter?: string })
                  .shoppable_content_filter ?? '',
              ).trim() || undefined,
            };
            // Recent Content Latest/Series tabs always stay in the carousel list
            // (even if also referenced by the home featured collection).
            if (isCarouselLatestTab(tabShape) || isCarouselSeriesTab(tabShape)) {
              return true;
            }
            return !isComponentClaimed(claimedComponents, c);
          })
          .map((comp, i) => {
            // Automated day-wise / trending rows live on Recent Content in the
            // Master Template (not Shop), even though LiB marks some as shoppable.
            // Product-named collections (Trending This Month / Shop This Past Week)
            // stay typed as shop so navigation opens the Products page.
            const shop =
              (isShoppable(comp) ||
                isWebsiteProductCollectionCarousel({
                  name: stayuptodateTextField(comp.name),
                  title: stayuptodateTextField(comp.display_name),
                  type: String((comp as { type?: string }).type ?? ""),
                  details: String(
                    (comp as { details?: string }).details ?? "",
                  ),
                  shoppable_content_filter: String(
                    (comp as { shoppable_content_filter?: string })
                      .shoppable_content_filter ?? "",
                  ),
                })) &&
              !isAutomatedRecentContentCarousel(comp);
            const isSeriesRow =
              (comp.type ?? '').trim() === CONTENT_SERIES_COMPONENT_TYPE;
            // Treat all non-shoppable, non-series components as content (Latest tab).
            const content = !shop && !isSeriesRow;
            const items = content ? contentItemPool.slice(0, 6) : shopItemPool.slice(0, 6);
            // Normalize post_ids — component stores it as comma-separated string or array
            const rawPostIds = (comp as { post_ids?: unknown }).post_ids;
            const postIds: string = Array.isArray(rawPostIds)
              ? (rawPostIds as unknown[]).map(String).join(',')
              : typeof rawPostIds === 'string'
                ? rawPostIds
                : '';
            const rawComp = comp as {
              type?: string;
              content_order?: string;
              custom_order?: string;
              content_shape?: string;
              content_sound?: boolean;
              shoppable_status_filter?: string;
              route_name?: string;
              unique_id?: string;
              query?: string;
              details?: string;
              description?: string;
              card_image?: unknown;
            };
            const nameFallback = stayuptodateTextField(comp.name);
            const rowId =
              comp.id != null && String(comp.id).trim() !== ''
                ? String(comp.id).trim()
                : '';
            // Unpublished feed edits (platform / Shoppable) must survive refetch
            // the same way banner image/details do — otherwise Instagram Shoppable
            // snaps back when switching to TikTok before Publish.
            const pending = rowId
              ? getWebsiteTemplateDirtyPatch(rowId)
              : undefined;
            const pendingType =
              typeof pending?.type === 'string' ? pending.type : undefined;
            const pendingDisplayName =
              typeof pending?.display_name === 'string'
                ? pending.display_name
                : undefined;
            const pendingOrder =
              typeof pending?.content_order === 'string'
                ? pending.content_order
                : undefined;
            const pendingSound =
              typeof pending?.content_sound === 'boolean'
                ? pending.content_sound
                : undefined;
            const pendingStatus =
              typeof pending?.shoppable_status_filter === 'string'
                ? pending.shoppable_status_filter
                : undefined;
            const pendingContentFilter =
              typeof pending?.shoppable_content_filter === 'string'
                ? pending.shoppable_content_filter
                : undefined;
            const pendingDetails =
              typeof pending?.details === 'string' ? pending.details : undefined;
            const pendingQuery =
              typeof pending?.query === 'string' ? pending.query : undefined;
            const pendingPostIds = pending?.post_ids;
            const pendingCustomOrder =
              typeof pending?.custom_order === 'string'
                ? pending.custom_order
                : undefined;
            const resolvedType = pendingType ?? rawComp.type ?? '';
            const resolvedPostIds: string = Array.isArray(pendingPostIds)
              ? (pendingPostIds as unknown[]).map(String).join(',')
              : typeof pendingPostIds === 'string'
                ? pendingPostIds
                : postIds;
            const routeOrId =
              (rawComp.route_name && String(rawComp.route_name).trim()) ||
              (rawComp.unique_id && String(rawComp.unique_id).trim()) ||
              '';
            const renderSlug = String(
              routeOrId ||
                rowId ||
                (isSeriesRow && nameFallback
                  ? slugifyShopCategory(nameFallback) || nameFallback
                  : nameFallback) ||
                '',
            ).trim();
            return {
              // Prefer opaque row/slug identity — same title must not collapse
              // two carousels onto one list entry.
              id: routeOrId || rowId || String(comp.name ?? i),
              title:
                stayuptodateTextField(comp.name) ||
                stayuptodateTextField(comp.display_name) ||
                (isSeriesRow ? 'Series' : shop ? 'Shop' : 'Content'),
              subtitle:      stayuptodateTextField(comp.cta_placeholder) || '',
              items,
              type:          content ? 'content' : 'shop',
              postIds: resolvedPostIds,
              componentType: resolvedType,
              componentDisplayName:
                String(pendingDisplayName ?? comp.display_name ?? '').trim() ||
                undefined,
              contentOrder:  pendingOrder ?? rawComp.content_order ?? 'DESC',
              customOrder:
                pendingCustomOrder ?? rawComp.custom_order ?? '',
              contentShape:  rawComp.content_shape ?? 'rect',
              contentSound:
                pendingSound !== undefined
                  ? pendingSound
                  : rawComp.content_sound !== false,
              shoppableStatusFilter:
                String(
                  pendingStatus ??
                    rawComp.shoppable_status_filter ??
                    'all',
                ).trim() || 'all',
              removedPostIds: (() => {
                const raw = (comp as { removed_postids?: unknown }).removed_postids;
                if (Array.isArray(raw)) {
                  return raw.map(String).map((s) => s.trim()).filter(Boolean).join(',');
                }
                if (typeof raw === 'string') return raw.trim() || undefined;
                return undefined;
              })(),
              query:
                String(pendingQuery ?? rawComp.query ?? '').trim() || undefined,
              details:
                String(pendingDetails ?? rawComp.details ?? '').trim() ||
                undefined,
              description:
                String(rawComp.description || '').trim() ||
                (String(resolvedType || '').toLowerCase() === 'links'
                  ? parseCollectionDescriptionFromDetails(
                      pendingDetails ?? rawComp.details,
                    )
                  : '') ||
                undefined,
              coverImage:
                pickFirstNonEmptyImageField(rawComp.card_image) || undefined,
              renderSlug:    renderSlug || undefined,
              stayComponentRowId: rowId || undefined,
              shoppableContentFilter: String(
                pendingContentFilter ??
                  (comp as { shoppable_content_filter?: string })
                    .shoppable_content_filter ??
                  '',
              ).trim() || undefined,
              isEmpty:
                (comp as { isEmpty?: unknown }).isEmpty === true ||
                (comp as { isEmpty?: unknown }).isEmpty === 'true',
            };
          });

        // ── Featured carousel block (homepage `CarouselSection`) ─────
        // Preferred source: the top-index `carousel-collection` component,
        // whose `post_ids` reference other stayuptodate components. Each
        // navigable member becomes a clickable card that deep-links to its
        // Shop or Recent tab via `onNavigate(page, { carouselSlug })`.
        // Fallback: if no carousel-collection exists, render the first
        // navigable component as a single card.
        type RawComponentRow = {
          id?:           string | number;
          name?:         string;
          display_name?: string;
          type?:         string;
          details?:      string;
          shoppable_content_filter?: string;
          card_image?:   unknown;
          route_name?:   string;
          unique_id?:    string;
          post_ids?:     unknown;
        };
        const compToKind = (c: RawComponentRow): 'shop' | 'content' | null => {
          if (
            isWebsiteProductCollectionCarousel({
              name: stayuptodateTextField(c.name),
              title: stayuptodateTextField(c.display_name),
              type: String(c.type ?? ""),
              details: String(c.details ?? ""),
              shoppable_content_filter: String(
                c.shoppable_content_filter ?? "",
              ),
            }) ||
            (isShoppable(c) && !isAutomatedRecentContentCarousel(c))
          ) {
            return 'shop';
          }
          if (isContent(c) || isAutomatedRecentContentCarousel(c)) return 'content';
          return null;
        };
        const compToSlug = (c: RawComponentRow): string =>
          String(
            (c.route_name && String(c.route_name).trim()) ||
              (c.unique_id && String(c.unique_id).trim()) ||
              (c.name && String(c.name).trim()) ||
              '',
          ).trim();
        // Card/section title uses `component.name`. We still fall back to
        // `display_name` so a misconfigured row never renders an empty card.
        const compToTitle = (c: RawComponentRow): string =>
          String(c.name ?? '').trim() || String(c.display_name ?? '').trim();
        const compToFeaturedItem = (c: RawComponentRow): FeaturedCarouselItem | null => {
          const kind = compToKind(c);
          const slug = compToSlug(c);
          if (!kind || !slug) return null;
          const rowId =
            c.id != null && String(c.id).trim() !== ""
              ? String(c.id).trim()
              : "";
          // Prefer full card-cover resolution (card/table/product/thumb/media) —
          // card_image alone left many Group members with empty thumbs and a
          // permanent skeleton in FeaturedCarouselCardMedia.
          const image =
            getFirstUsableImageUrlForCard(c) ||
            pickFirstNonEmptyImageField(c.card_image) ||
            "";
          const platform = platformKeyFromComponentRow(c);
          return {
            id:    String(c.id ?? c.name ?? slug),
            title: compToTitle(c),
            image,
            slug,
            kind,
            ...(platform ? { platform } : {}),
            ...(!image ? { isText: true as const } : {}),
            ...(rowId ? { stayComponentRowId: rowId } : {}),
          };
        };

        const allTemplateRows = [
          ...(((carouselRes as { data?: RawComponentRow[] }).data ?? [])),
          ...((showingComponents as RawComponentRow[]) || []),
          ...(((carouselRes as { archiveComponents?: RawComponentRow[] })
            .archiveComponents ?? [])),
        ];
        const rowById = new Map<string, RawComponentRow>();
        allTemplateRows.forEach((row) => {
          if (row.id != null) rowById.set(String(row.id), row);
        });
        const memberIdsOf = (comp: RawComponentRow): string[] => {
          const pending = getWebsiteTemplateDirtyPatch(comp.id);
          const rawIds =
            pending && pending.post_ids != null
              ? pending.post_ids
              : comp.post_ids;
          if (Array.isArray(rawIds)) {
            return (rawIds as unknown[])
              .map((v) => String(v).trim())
              .filter(Boolean);
          }
          if (typeof rawIds === "string") {
            return rawIds.split(",").map((s) => s.trim()).filter(Boolean);
          }
          return [];
        };
        const itemsForCollection = (comp: RawComponentRow): FeaturedCarouselItem[] => {
          const memberItems: FeaturedCarouselItem[] = [];
          for (const id of memberIdsOf(comp)) {
            const row = rowById.get(id);
            if (row) {
              const fromRow = compToFeaturedItem(row);
              if (fromRow) {
                memberItems.push(fromRow);
                continue;
              }
              const image =
                getFirstUsableImageUrlForCard(row) ||
                pickFirstNonEmptyImageField(row.card_image) ||
                "";
              const platform = platformKeyFromComponentRow(row);
              memberItems.push({
                id: String(row.id ?? id),
                title: compToTitle(row) || String(id),
                image,
                slug: compToSlug(row) || String(id),
                kind: "content" as const,
                stayComponentRowId: String(row.id ?? id),
                ...(platform ? { platform } : {}),
                ...(!image ? { isText: true as const } : {}),
              });
              continue;
            }
            memberItems.push({
              id,
              title: String(id),
              image: "",
              slug: id,
              kind: "content",
              stayComponentRowId: id,
              isText: true,
            });
          }
          return memberItems;
        };

        const featuredCollections: FeaturedCarouselData[] = (() => {
          const seen = new Set<string>();
          const out: FeaturedCarouselData[] = [];
          const pool = [
            ...(((carouselRes as { data?: RawComponentRow[] }).data ?? [])),
            ...((showingComponents as RawComponentRow[]) || []),
          ];
          for (const c of pool) {
            const type = String(c.type ?? "").trim().toLowerCase();
            if (type !== "carousel-collection" && type !== "collection-carousel") {
              continue;
            }
            const st = String(
              (c as { status_expiry?: string; status?: string }).status_expiry ??
                (c as { status?: string }).status ??
                "",
            )
              .toLowerCase()
              .trim();
            if (st === "deleted" || st === "hidden") continue;
            const row = c as RawComponentRow;
            const id =
              row.id != null && String(row.id).trim() !== ""
                ? String(row.id).trim()
                : "";
            if (id && seen.has(id)) continue;
            if (id) seen.add(id);
            out.push({
              mode: "collection" as const,
              title: compToTitle(row),
              renderSlug: compToSlug(row),
              stayComponentRowId: id || undefined,
              items: itemsForCollection(row),
            });
          }
          return out;
        })();

        let featuredCarousel: FeaturedCarouselData | null = null;
        const collectionComp = showingComponents.find(
          (c) =>
            (c.type ?? '').trim() === 'carousel-collection' &&
            !isComponentClaimed(claimedComponents, c),
        ) as RawComponentRow | undefined;

        if (collectionComp && claimComponent(claimedComponents, collectionComp)) {
          // Same pending post_ids overlay as featuredCollections (memberIdsOf).
          const memberIds = memberIdsOf(collectionComp);
          const allRows = (carouselRes as { data?: RawComponentRow[] }).data ?? [];
          const byId = new Map<string, RawComponentRow>();
          allRows.forEach((row) => {
            if (row.id != null) byId.set(String(row.id), row);
          });
          const memberItems: FeaturedCarouselItem[] = [];
          for (const id of memberIds) {
            const row = byId.get(id);
            if (!row || !isNavigable(row) || isComponentClaimed(claimedComponents, row)) {
              continue;
            }
            const item = compToFeaturedItem(row);
            if (!item) continue;
            claimComponent(claimedComponents, row);
            memberItems.push(item);
          }

          if (memberItems.length > 0) {
            featuredCarousel = {
              mode: 'collection',
              title: compToTitle(collectionComp),
              renderSlug: compToSlug(collectionComp),
              items: memberItems,
            };
          }
        }

        // ── Map: About (phase 1a — user + template_components only) ────
        // headshot preference (finalized in merge):
        //   user.about_image → template_settings.about.headshot → picture/storage_url
        // story[]: about_para first; optional legacy API merge in phase 1b
        // values[]: template_components content tabs (More From)
        const storyFromAboutPara = normalizeAboutParaToStory(userRow?.about_para);
        let story: string[] =
          storyFromAboutPara.length > 0 ? [...storyFromAboutPara] : [];
        const templateRows = (carouselRes as { data?: unknown[] }).data ?? [];
        const aboutValues = buildAboutMoreFromValuesFromTemplateComponents(
          templateRows,
        );

        const profileName =
          typeof userRow?.name === 'string' ? userRow.name.trim() : '';
        const aboutBio =
          typeof userRow?.description === 'string' ? userRow.description : '';
        const aboutImage = String(userRow?.about_image || "").trim();

        let about: CreatorData['about'] = {
          aboutImage,
          // Live default before merge: about_image, else shared profile URL.
          headshot: aboutImage || profilePicture,
          bio: aboutBio,
          story,
          values: aboutValues,
          email: '',
          phone: '',
          address: '',
          hours: '',
          profileName,
        };

        // Unlock homepage Featured Carousel strip fetches before LiB/links/contact.
        const earlyHasData: HasData = {
          bio: !!(bio.name || bio.avatar),
          highlights: false,
          banners: !!(
            liveBanners?.hero?.image ||
            liveBanners?.promo?.image ||
            liveBanners?.footer?.image ||
            (liveBanners?.catalog || []).some((b) =>
              String(b?.image || "").trim(),
            )
          ),
          highlightCards: highlightCards.length > 0,
          links: false,
          carousels:
            carousels.length > 0 ||
            (featuredCarousel != null && featuredCarousel.items.length > 0),
          press: false,
          about: !!(about.headshot || story.length > 0 || aboutValues.length > 0),
          products: false,
          journal: false,
          recent: false,
          campaign: campaignBlocks.length > 0,
          contact: false,
          linkInBio: false,
        };

        // Leave `contact` unset on first load (undefined → ContactForm skeleton).
        // On background refresh, keep the prior settled value until contact_page
        // re-applies so the form / nav do not flash.
        setLiveData((prev) => ({
          bio,
          highlights,
          banners: liveBanners,
          highlightCards,
          links: [],
          linksTitle,
          linksCollectionName: topLinksCollection || undefined,
          linkInBioStack: [],
          products,
          journal: [],
          carousels,
          featuredCarousel,
          featuredCollections,
          about,
          ...(prev.contact !== undefined ? { contact: prev.contact } : {}),
          ...(campaignRenderSlug ? { campaignRenderSlug } : {}),
          ...(campaignId ? { campaignId } : {}),
          ...(campaignName ? { campaignName } : {}),
          ...(campaignDescription ? { campaignDescription } : {}),
          ...(campaignButtonText ? { campaignButtonText } : {}),
          ...(campaignFields.length > 0 ? { campaignFields } : {}),
          campaignBlocks,
          emailCampaignsEnabled,
          ...(flodeskPopupFormId ? { flodeskPopupFormId } : {}),
        }));
        setHasData((prev) => ({
          ...earlyHasData,
          // Keep prior contact flag on refresh until contact_page remaps.
          contact:
            prev.contact && earlyHasData.contact === false
              ? prev.contact
              : earlyHasData.contact,
        }));
        if (!cancelled) {
          liveDataLoadedForUserRef.current = uid;
          setLoading(false);
        }

        // ── Phase 1b + 2 network: start only after phase 1a unlocks ────
        // Yield a frame so Bio/nav can paint before StayUpToDate / products contend.
        await new Promise<void>((resolve) => {
          if (typeof window === "undefined") {
            resolve();
            return;
          }
          window.requestAnimationFrame(() => resolve());
        });
        if (cancelled) return;

        // Journal after unlock — parallel with phase 1b, never gates home.
        journalPromiseStarted = true;
        const journalPromise = timed(
          "blog_posts",
          fetchJournalForCreator(usernameLc ?? '', userId!, displayName, {
            includeDrafts,
          }),
        );

        const emptyStayUpToDate = {
          order: [] as unknown[],
          data: [] as unknown[],
          showingComponents: [] as unknown[],
          archiveComponents: [] as unknown[],
        };

        // linksPromise + contactPromise already started alongside phase 1a.
        const knownActiveCampaign = Boolean(userRow?.active_campaign);

        // Collection deep-links / deferred home: skip StayUpToDate so strip RPCs
        // own the network. When not deferred, wait for links (already in flight)
        // so we can pass landing-page rows and skip the enrich fetch.
        const stayUpToDatePromise = timed(
          "stayuptodate_components (+active_campaign)",
          deferHeavyPools || !usernameLc
            ? Promise.resolve(emptyStayUpToDate)
            : linksPromise
                .then((linksResAll) => {
                  const allRows = (linksResAll?.data ?? []) as RawLinkRow[];
                  const landingPageLinks = allRows
                    .filter((l) => String(l.type ?? '') === 'landing-page')
                    .map((l) => ({
                      collection_name: l.collection_name ?? null,
                      parent_id: l.parent_id ?? null,
                    }));
                  return getStayUpToDateComponents(usernameLc, supabase, userId, {
                    activeCampaign: knownActiveCampaign,
                    landingPageLinks,
                  });
                })
                .catch(() =>
                  // Links failed — still load stay; enrich will fetch landing links itself.
                  getStayUpToDateComponents(usernameLc, supabase, userId, {
                    activeCampaign: knownActiveCampaign,
                  }),
                ),
        );

        const aboutPromise: Promise<unknown> = Promise.resolve(null);

        // Apply Contact as soon as contact_page resolves — do not wait for
        // StayUpToDate / links. Contact deep-links only need this row (+ email fallback).
        void contactPromise.then((contactResEarly) => {
          if (cancelled) return;
          const contactEarly = mapContactPageConfig(
            (contactResEarly?.data ?? null) as RawContactPageRow,
            userRow,
          );
          setLiveData((prev) => ({ ...prev, contact: contactEarly }));
          setHasData((prev) => ({
            ...prev,
            contact: Boolean(contactEarly?.enabled),
          }));
        });

        const phase1bPromise = Promise.all([
          linksPromise,
          stayUpToDatePromise,
          aboutPromise,
          contactPromise,
        ]) as Promise<
          [
            { data: RawLinkRow[] | null },
            Awaited<ReturnType<typeof getStayUpToDateComponents>>,
            unknown,
            { data: RawContactPageRow },
          ]
        >;

        const productsPromise = timed(
          "product_bank",
          deferHeavyPools
            ? Promise.resolve({ data: [] as RawProductRow[] | null })
            : supabase
                .from('product_bank')
                .select('product_id,name,url,image,query')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(20),
        );

        const usernameProductsPromise = timed(
          `username_table(${usernameLc}).PRODUCT count`,
          deferHeavyPools || !usernameLc
            ? Promise.resolve({ count: 0, error: null })
            : supabase
                .from(usernameLc)
                .select('post_id', { count: 'exact', head: true })
                .eq('user_id', userId)
                .eq('type', 'PRODUCT')
                .eq('index', 'active'),
        );

        const contentPromise = timed(
          "content_instagram IMAGE/VIDEO",
          deferHeavyPools
            ? Promise.resolve({ data: [] as RawContentRow[] | null })
            : supabase
                .from('content_instagram')
                .select('post_id,caption,storage_url,thumbnail,post_date,type')
                .eq('user_id', userId)
                .in('type', ['IMAGE', 'VIDEO'])
                .order('post_date', { ascending: false })
                .limit(12),
        );

        const feedFiltersPromise = timed(
          "rpc get_general_feed_count_content",
          deferHeavyPools
            ? Promise.resolve(null)
            : Promise.resolve(
                supabase.rpc('get_general_feed_count_content', {
                  userid: userId,
                  filter: StaticVariables().TYPES,
                  query_str: '',
                  user_name: usernameLc,
                  socials: [
                    'instagram', 'youtube', 'tiktok', 'podcast', 'pinterest',
                    'newsletter', 'twitter', 'linkedin', 'MISC-content',
                  ],
                  index_type: 'active',
                }),
              )
                .then((r) => r as { data?: { posts?: FeedFilterPost[] }[] })
                .catch(() => null),
        );

        const latestStoryPromise = timed(
          "content_instagram STORY/HIGHLIGHTS",
          deferHeavyPools
            ? Promise.resolve(null)
            : Promise.resolve(
                supabase
                  .from('content_instagram')
                  .select('post_id, storage_url, thumbnail, post_date, type, caption')
                  .eq('user_id', userId)
                  .in('type', ['STORY', 'HIGHLIGHTS'])
                  .order('post_date', { ascending: false })
                  .limit(1)
                  .maybeSingle(),
              )
                .then((r) => r?.data ?? null)
                .catch(() => null),
        );

        // When heavy pools were skipped for a Products/Content landing or
        // collection deep-link, warm them on idle so LiB / highlights / products
        // still fill if the visitor stays or navigates to Home.
        if (deferHeavyPools && !cancelled) {
          const warmHeavy = () => {
            if (cancelled || !usernameLc) return;
            void getStayUpToDateComponents(usernameLc, supabase, userId, {
              activeCampaign: Boolean(userRow?.active_campaign),
            }).then(
              (stay) => {
                if (cancelled) return;
                setLiveData((prev) => ({
                  ...prev,
                  linkInBioStack: [...(stay.showingComponents ?? [])],
                }));
                setHasData((prev) => ({
                  ...prev,
                  linkInBio: (stay.showingComponents ?? []).length > 0,
                }));
              },
            );
            void Promise.all([
              supabase
                .from('product_bank')
                .select('product_id,name,url,image,query')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(20),
              supabase
                .from(usernameLc)
                .select('post_id', { count: 'exact', head: true })
                .eq('user_id', userId)
                .eq('type', 'PRODUCT')
                .eq('index', 'active'),
              supabase
                .from('content_instagram')
                .select('post_id,caption,storage_url,thumbnail,post_date,type')
                .eq('user_id', userId)
                .in('type', ['IMAGE', 'VIDEO'])
                .order('post_date', { ascending: false })
                .limit(12),
              Promise.resolve(
                supabase.rpc('get_general_feed_count_content', {
                  userid: userId,
                  filter: StaticVariables().TYPES,
                  query_str: '',
                  user_name: usernameLc,
                  socials: [
                    'instagram', 'youtube', 'tiktok', 'podcast', 'pinterest',
                    'newsletter', 'twitter', 'linkedin', 'MISC-content',
                  ],
                  index_type: 'active',
                }),
              )
                .then((r) => r as { data?: { posts?: FeedFilterPost[] }[] })
                .catch(() => null),
              Promise.resolve(
                supabase
                  .from('content_instagram')
                  .select('post_id, storage_url, thumbnail, post_date, type, caption')
                  .eq('user_id', userId)
                  .in('type', ['STORY', 'HIGHLIGHTS'])
                  .order('post_date', { ascending: false })
                  .limit(1)
                  .maybeSingle(),
              )
                .then((r) => r?.data ?? null)
                .catch(() => null),
            ]).then(
              ([productsRes, usernameProductsRes, contentRes, feedFiltersRes, latestStoryRes]) => {
                if (cancelled) return;
                const highlights2 = buildHighlightsFromFeed(
                  feedFiltersRes,
                  latestStoryRes as LatestStoryRow,
                );
                const rawProducts2 = (productsRes.data ?? []) as RawProductRow[];
                const products2 = mapProductBankRows(rawProducts2);
                const rawContent2 = (contentRes.data ?? []) as RawContentRow[];
                const contentItemPool2 = mapContentItemPool(rawContent2);
                const shopItemPool2 = mapShopItemPool(rawProducts2);
                const liveProductCount2 =
                  typeof usernameProductsRes.count === 'number'
                    ? usernameProductsRes.count
                    : 0;
                setLiveData((prev) => ({
                  ...prev,
                  highlights: highlights2,
                  products: products2,
                  carousels: (prev.carousels ?? []).map((c) => ({
                    ...c,
                    items:
                      c.type === 'content'
                        ? contentItemPool2.slice(0, 6)
                        : shopItemPool2.slice(0, 6),
                  })),
                }));
                setHasData((prev) => ({
                  ...prev,
                  highlights: highlights2.length > 0,
                  products: liveProductCount2 > 0 || products2.length > 0,
                  recent: Boolean(prev.journal) || contentItemPool2.length > 0,
                }));
              },
            );
          };
          const w = window as Window & {
            requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
          };
          if (typeof w.requestIdleCallback === 'function') {
            w.requestIdleCallback(warmHeavy, { timeout: 4000 });
          } else {
            globalThis.setTimeout(warmHeavy, 1500);
          }
        }

        // Defer card_image sync so it does not contend with strip RPCs.
        // AboutValues is not mounted in PAGES.about — skip More From warm sync.
        // Featured + Group-extra (`featuredCollections`) member covers need idle warm.
        const featuredSyncTargets = [
          ...(featuredCarousel?.items ?? []),
          ...featuredCollections.flatMap((col) => col.items ?? []),
        ]
          .map((it) => it.stayComponentRowId?.trim())
          .filter((id): id is string => Boolean(id))
          .map((stayComponentRowId) => ({ stayComponentRowId }));

        const cardImageSyncTargets = featuredSyncTargets.filter(
          (v, i, arr) =>
            arr.findIndex(
              (x) => x.stayComponentRowId === v.stayComponentRowId,
            ) === i,
        );

        if (userId && usernameLc && cardImageSyncTargets.length > 0) {
          const runCardSync = () => {
            if (cancelled) return;
            syncAboutMoreFromTemplateCardImagesInBackground(
              userId,
              usernameLc,
              cardImageSyncTargets,
            );
          };
          if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            (
              window as Window & {
                requestIdleCallback: (
                  cb: () => void,
                  opts?: { timeout?: number },
                ) => number;
              }
            ).requestIdleCallback(runCardSync, { timeout: 2500 });
          } else {
            globalThis.setTimeout(runCardSync, 600);
          }
        }

        // ── Phase 1b: links + LiB + contact (no longer gates strip start) ─
        const [linksRes, stayUpToDateRes, aboutRes, contactRes] =
          await phase1bPromise;
        if (cancelled) return;

        // Website Top Links only — landing-page rows were for stay enrich.
        rawLinks = ((linksRes.data ?? []) as RawLinkRow[]).filter(
          (l) => String(l.type ?? "") === "website-template",
        );
        links = topLinksSectionId || topLinksCollection
          ? rawLinks
              .filter((l) => {
                const parentId = String(l.parent_id ?? "").trim();
                if (topLinksSectionId && parentId === topLinksSectionId) {
                  return true;
                }
                if (
                  !parentId &&
                  topLinksCollection &&
                  String(l.collection_name ?? "").trim() === topLinksCollection
                ) {
                  return true;
                }
                return false;
              })
              .map((l) => ({
                id: String(l.id),
                title: l.link_name ?? "",
                subtitle: "",
                url: l.link_url ?? "#",
                thumb: l.image_url ?? "",
              }))
          : [];

        const res = aboutRes as Record<string, unknown> | null;
        const rawStory =
          (Array.isArray(res?.story) ? res!.story : null) ??
          (Array.isArray(res?.paragraphs) ? res!.paragraphs : null) ??
          (Array.isArray(res?.about) ? res!.about : null) ??
          (typeof res?.text === "string" ? [res!.text] : null);
        if (story.length === 0 && rawStory) {
          story = (rawStory as unknown[])
            .filter((s) => typeof s === "string" && (s as string).trim())
            .map((s) => s as string);
          about = { ...about, story };
        }

        // ── Map: Contact page config ──────────────────────────────────
        // ContactMeModal stores: field_config.fields = { Email: true, Message: true, ... }
        // Usually already applied via contactPromise.then above; re-apply here so the
        // full Phase 1b setLiveData snapshot stays consistent.
        const contact = mapContactPageConfig(
          (contactRes?.data ?? null) as RawContactPageRow,
          userRow,
        );
        const contactHasData = Boolean(contact?.enabled);

        // Do not await journal before first paint — home (and most pages) never
        // need blog posts to leave skeleton. Journal UI keeps its own pending flag.
        const computed: HasData = {
          bio:        !!(bio.name || bio.avatar),
          highlights: highlights.length > 0,
          banners:
            !!(liveBanners?.hero?.image || liveBanners?.promo?.image || liveBanners?.footer?.image) ||
            (liveBanners?.catalog || []).some((b) =>
              Boolean(String(b?.image || "").trim()),
            ),
          highlightCards: highlightCards.length > 0,
          links:      links.length > 0,
          carousels:
            carousels.length > 0 ||
            (featuredCarousel != null && featuredCarousel.items.length > 0),
          press:      false,  // comes from template_settings only
          about:      !!(about.headshot || story.length > 0 || aboutValues.length > 0),
          products:   products.length > 0,
          journal:    false,
          recent:     contentItemPool.length > 0,
          campaign:   campaignBlocks.length > 0,
          contact:    contactHasData,
          linkInBio:  (stayUpToDateRes.showingComponents ?? []).length > 0,
        };

        const linkInBioStack = [...(stayUpToDateRes.showingComponents ?? [])];

        setLiveData({
          bio,
          highlights,
          banners: liveBanners,
          highlightCards,
          links,
          linksTitle,
          linksCollectionName: topLinksCollection || undefined,
          linkInBioStack,
          products,
          journal: [],
          carousels,
          featuredCarousel,
          featuredCollections,
          about,
          contact,
          ...(campaignRenderSlug ? { campaignRenderSlug } : {}),
          ...(campaignId ? { campaignId } : {}),
          ...(campaignName ? { campaignName } : {}),
          ...(campaignDescription ? { campaignDescription } : {}),
          ...(campaignButtonText ? { campaignButtonText } : {}),
          ...(campaignFields.length > 0 ? { campaignFields } : {}),
          campaignBlocks,
          emailCampaignsEnabled,
          ...(flodeskPopupFormId ? { flodeskPopupFormId } : {}),
        });
        setHasData(computed);

        if (!cancelled) {
          liveDataLoadedForUserRef.current = uid;
          setLoading(false);
        }

        // Attach journal as soon as it resolves (parallel with phase 2).
        void (async () => {
          try {
            journal = await journalPromise;
          } catch (blogErr) {
            console.warn('[CreatorDataDB] journal fetch failed:', blogErr);
            journal = [];
          }
          if (cancelled) return;
          setLiveData((prev) => ({ ...prev, journal }));
          setHasData((prev) => ({
            ...prev,
            journal: journal.length > 0,
            recent: journal.length > 0 || prev.recent,
          }));
          setJournalLoading(false);
        })();

        // Collection deep-links: heavy pools warm on idle above — skip empty phase-2 merge.
        if (deferHeavyPools) {
          if (campaignId) {
            const { data: campaignRow } = await supabase
              .from('creators_campaigns')
              .select('campaign_name, campaign_button_text, metadata')
              .eq('id', campaignId)
              .maybeSingle();
            if (!cancelled) {
              const mergedCampaign = applyCreatorsCampaignRow(campaignRow, {
                campaignName,
                campaignButtonText,
                campaignDescription,
                campaignFields,
              });
              setLiveData((prev) => ({
                ...prev,
                ...(mergedCampaign.campaignName
                  ? { campaignName: mergedCampaign.campaignName }
                  : {}),
                ...(mergedCampaign.campaignDescription
                  ? { campaignDescription: mergedCampaign.campaignDescription }
                  : {}),
                ...(mergedCampaign.campaignButtonText
                  ? { campaignButtonText: mergedCampaign.campaignButtonText }
                  : {}),
                ...(mergedCampaign.campaignFields.length > 0
                  ? { campaignFields: mergedCampaign.campaignFields }
                  : {}),
              }));
            }
          }
          return;
        }

        // ── Phase 2: RPC, products, stories, live campaign ──
        const phase2Results = await Promise.all([
          productsPromise,
          usernameProductsPromise,
          contentPromise,
          feedFiltersPromise,
          latestStoryPromise,
        ]);
        const productsRes = phase2Results[0] as { data?: RawProductRow[] | null };
        const usernameProductsRes = phase2Results[1] as { count?: number | null };
        const contentRes = phase2Results[2] as { data?: RawContentRow[] | null };
        const feedFiltersRes = phase2Results[3];
        const latestStoryRes = phase2Results[4];
        if (cancelled) return;

        if (campaignId) {
          const { data: campaignRow } = await supabase
            .from('creators_campaigns')
            .select('campaign_name, campaign_button_text, metadata')
            .eq('id', campaignId)
            .maybeSingle();
          const mergedCampaign = applyCreatorsCampaignRow(campaignRow, {
            campaignName,
            campaignButtonText,
            campaignDescription,
            campaignFields,
          });
          campaignName = mergedCampaign.campaignName;
          campaignButtonText = mergedCampaign.campaignButtonText;
          campaignDescription = mergedCampaign.campaignDescription;
          campaignFields = mergedCampaign.campaignFields;
        }

        const highlights2 = buildHighlightsFromFeed(
          feedFiltersRes,
          latestStoryRes as LatestStoryRow,
        );
        const rawProducts2 = (productsRes.data ?? []) as RawProductRow[];
        const products2 = mapProductBankRows(rawProducts2);
        const rawContent2 = (contentRes.data ?? []) as RawContentRow[];
        const contentItemPool2 = mapContentItemPool(rawContent2);
        const shopItemPool2 = mapShopItemPool(rawProducts2);
        const liveProductCount2 =
          typeof usernameProductsRes.count === 'number'
            ? usernameProductsRes.count
            : 0;

        if (cancelled) return;
        setLiveData((prev) => ({
          ...prev,
          highlights: highlights2,
          products: products2,
          carousels: (prev.carousels ?? []).map((c) => ({
            ...c,
            items:
              c.type === 'content'
                ? contentItemPool2.slice(0, 6)
                : shopItemPool2.slice(0, 6),
          })),
          ...(campaignName ? { campaignName } : {}),
          ...(campaignDescription ? { campaignDescription } : {}),
          ...(campaignButtonText ? { campaignButtonText } : {}),
          ...(campaignFields.length > 0 ? { campaignFields } : {}),
        }));
        setHasData((prev) => ({
          ...prev,
          highlights: highlights2.length > 0,
          products: liveProductCount2 > 0 || products2.length > 0,
          recent: Boolean(prev.journal) || contentItemPool2.length > 0,
        }));
      } catch (err) {
        console.error('[CreatorDataDB] fetch error:', err);
        if (!cancelled && journal.length > 0) {
          setLiveData((prev) => ({ ...prev, journal }));
          setHasData((prev) => ({
            ...prev,
            journal: true,
            recent: true,
          }));
        }
        // Journal has its own finally via the attach IIFE; if that never ran
        // (thrown before journalPromise was created), clear the loading flag.
        if (!cancelled && !journalPromiseStarted) {
          setJournalLoading(false);
        }
      } finally {
 
        if (!cancelled) {
          liveDataLoadedForUserRef.current = uid;
          setLoading(false);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [userId, usernameLc, liveDataRefreshNonce, enabled, includeDrafts, deferHeavyPools]); // eslint-disable-line react-hooks/exhaustive-deps

  const uid = userId?.trim() || '';
  // Synchronous guard: `useLayoutEffect`/`useEffect` flip `loading` one frame late when
  // `userId` first resolves — that frame used to mount the template shell and flash
  // StayUpToDate feed loaders before `TemplatePublicLoading`.
  const awaitingFirstLiveFetch =
    enabled &&
    Boolean(uid) &&
    !skipFetch &&
    liveDataLoadedForUserRef.current !== uid;
  const effectiveLoading = !enabled
    ? false
    : skipFetch
      ? false
      : awaitingFirstLiveFetch || loading;
  const effectiveJournalLoading = !enabled
    ? false
    : skipFetch
      ? false
      : awaitingFirstLiveFetch || journalLoading;

  return {
    liveData,
    hasData,
    loading: effectiveLoading,
    journalLoading: effectiveJournalLoading,
    browsableTemplateEditingStartedAt,
    touchStayComponentDetailImage,
    touchLibProfilePhoto,
    patchCarouselLiveFields,
    reorderFeaturedCollection,
    patchJournalLive,
    refreshLiveData,
  };
}
